import {memo, useCallback, useState} from 'react';
import {getPydanticError} from '~/common/utils/errors';
import {useCurrentGuestId} from '~/ViewInvitation/components/Guestlist/hooks/useCurrentGuestId';
import {usePostsApi} from '~/ViewInvitation/components/Guestlist/hooks/usePostsApi';
import type {PostBase, Post as PostType, UserData} from '~/ViewInvitation/types/api';
import {Button} from '~library/atoms/Button';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {HeartFilledIcon} from '~library/svg/v2/HeartFilledIcon';
import {HeartIcon} from '~library/svg/v2/HeartIcon';

import * as styles from './Likes.module.scss';

const hasLikedPost = (likedBy: UserData[], userId: string) =>
  !!likedBy?.find((l) => l.userId === userId);

interface LikesProps {
  likes: PostBase['likes'];
  likedBy: PostBase['likedBy'];
  postId: PostType['id'];
}

export const Likes = memo(({postId, likes, likedBy}: LikesProps) => {
  const {userId} = useCurrentGuestId();
  const {toggleLike} = usePostsApi();
  const [hasCurrentGuestLikedPost, setHasCurrentGuestLikedPost] = useState(() =>
    hasLikedPost(likedBy, userId)
  );

  const doLikeOrUnlike = useCallback(async () => {
    try {
      // I'm sure RTK has a better way to do these dependent optimistic updates
      setHasCurrentGuestLikedPost((prev) => !prev);
      await toggleLike(postId);
    } catch (e: unknown) {
      setHasCurrentGuestLikedPost((prev) => !prev);
      const action = hasCurrentGuestLikedPost ? 'unlike' : 'like';
      evite.error(`Failed to ${action} post`, e);
      const message = getPydanticError(e, '');
      toastAlert(`Unable to ${action} post.\n${message}`, {
        icon: <AlertLineIcon />,
        type: 'error',
      });
    }
  }, [postId, toggleLike, hasCurrentGuestLikedPost]);

  return (
    <div className={styles.likes}>
      <Button
        data-qa-id="like-button"
        variant="transparent"
        onClick={doLikeOrUnlike}
        disableProcessing
      >
        {hasCurrentGuestLikedPost ? (
          <HeartFilledIcon ratio={0.75} color="mexicanOpal" />
        ) : (
          <HeartIcon ratio={0.75} />
        )}
      </Button>
      <Typography variant="paragraph4" color="pebble">
        {likes}
      </Typography>
    </div>
  );
});

Likes.displayName = 'Likes';
